import { useCallback, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { useMutation } from '@apollo/client'
import { CalculateExpensesMileageDocument } from 'generated/resources/graphql'

import { formatStoredMileage } from 'modules/expenses/lib/numberFormating'

interface StopFormValue {
  name?: string | null
  latitude?: string | null
  longitude?: string | null
}

const EMPTY_STOPS: StopFormValue[] = []

interface CalculateMileageResponse {
  expensesMutations?: {
    calculateExpensesMileage?: {
      mileage?: {
        mileage?: number | null
        calculatedMileage?: number | null
      } | null
    } | null
  } | null
}

interface UseMileageDistanceCalculationParams {
  setIsCalculatingDistance?: (loading: boolean) => void
  hasExistingData: boolean
}

export const useMileageDistanceCalculation = ({
  setIsCalculatingDistance,
  hasExistingData
}: UseMileageDistanceCalculationParams) => {
  const { setValue, watch } = useFormContext()
  const [calculateExpensesMileage] = useMutation<CalculateMileageResponse>(
    CalculateExpensesMileageDocument
  )

  // Track if we're initializing with existing data
  const isInitializedRef = useRef(false)

  // Watch form values
  const originLatitude = watch('originLatitude')
  const originLongitude = watch('originLongitude')
  const destinationLatitude = watch('destinationLatitude')
  const destinationLongitude = watch('destinationLongitude')
  const units = watch('units')
  const watchedStops: StopFormValue[] | undefined = watch('stops')
  const stops = watchedStops ?? EMPTY_STOPS

  const roundTrip = watch('roundTrip')

  const recalculateDistance = useCallback(async () => {
    if (!destinationLatitude || !destinationLongitude || !originLatitude || !originLongitude) {
      return
    }

    if (!setIsCalculatingDistance) {
      return
    }

    setIsCalculatingDistance(true)

    try {
      const currentStops: StopFormValue[] = watch('stops') ?? EMPTY_STOPS
      const validStops = currentStops.filter(
        (s: StopFormValue): s is StopFormValue & { latitude: string; longitude: string } =>
          Boolean(s.latitude && s.longitude)
      )

      const result = await calculateExpensesMileage({
        variables: {
          destinationLatitude: parseFloat(destinationLatitude),
          destinationLongitude: parseFloat(destinationLongitude),
          originLatitude: parseFloat(originLatitude),
          originLongitude: parseFloat(originLongitude),
          roundTrip: Boolean(roundTrip),
          units,
          stops: validStops.map((stop, position) => ({
            latitude: parseFloat(stop.latitude),
            longitude: parseFloat(stop.longitude),
            position
          }))
        }
      })

      const mileageValue =
        result.data?.expensesMutations?.calculateExpensesMileage?.mileage?.mileage
      const calculatedMileageValue =
        result.data?.expensesMutations?.calculateExpensesMileage?.mileage?.calculatedMileage

      if (mileageValue != null && calculatedMileageValue != null) {
        setValue('mileage', formatStoredMileage(Number(mileageValue)), {
          shouldValidate: true,
          shouldDirty: true
        })
        setValue('calculatedMileage', formatStoredMileage(Number(calculatedMileageValue)), {
          shouldValidate: true,
          shouldDirty: true
        })
      }
    } finally {
      setIsCalculatingDistance(false)
    }
  }, [
    originLatitude,
    originLongitude,
    destinationLatitude,
    destinationLongitude,
    units,
    roundTrip,
    watch,
    setValue,
    setIsCalculatingDistance,
    calculateExpensesMileage
  ])

  // Serialized stops coords for dependency tracking in useEffect
  // Only include stops with valid coordinates so adding/removing empty stops doesn't trigger recalculation
  const stopsCoordsKey = stops
    .filter((s: StopFormValue) => s.latitude && s.longitude)
    .map((s: StopFormValue) => `${s.latitude},${s.longitude}`)
    .join('|')

  // Watch for changes in coordinates and recalculate distance when both sets are available
  useEffect(() => {
    // Skip initial calculation when editing existing data
    if (hasExistingData && !isInitializedRef.current) {
      isInitializedRef.current = true
      return
    }

    if (originLatitude && destinationLatitude) {
      recalculateDistance()
    }
  }, [
    originLatitude,
    destinationLatitude,
    units,
    roundTrip,
    hasExistingData,
    stopsCoordsKey,
    recalculateDistance
  ])

  return { stops }
}
